
<template>
  <div :class="[
      s.wrap,
      s[helper.displayDirection],
    ]"
    class="mx-2">
    <div class="my-2">
      <router-link :to="getBackRoute()">
        <button :class="[s.gray, s.button, s.clear, s.back]">
          <i class="fa-solid fa-chevrons-left me-3"/>
          {{ reservation ? '配車ページに戻る' : 'ホームに戻る' }}
        </button>
      </router-link>
    </div>
    <div :class="s.content" v-if="isOperatorChat || reservation">
      <div class="mt-2">
        <div :class="s.chat">
          <div :class="s.label" class="d-flex align-items-center justify-content-between">
            <div>
              <i class="fa-regular fa-comments me-1"/>{{ pageTitle }}
            </div>
            <div v-if="!chatType.includes('operator')">
              <button :class="[s.green, s.button]"  @click="updateUser"><i class="fa-regular fa-rotate"/></button>
            </div>
          </div>

          <!-- メッセージ送信フォーム（上部） -->
          <div :class="s.form_container">
            <ul
              v-if="helper.messageTemplates.length && !chatType.includes('operator')"
              :class="s.templates">
              <li
                :class="[s.gray, s.button, s.clear]"
                v-for="msg in helper.messageTemplates"
                :key="msg.id"
                @click="message = msg.label">{{ msg.label }}</li>
            </ul>
            <div :class="s.form">
              <textarea
                v-model="message"
                class="form-control"
                :class="s.textarea"
                rows="3"
                placeholder="メッセージを入力..."/>
              <div class="d-flex justify-content-end align-items-center mt-2">
                <button :class="[s.gray, s.button, s.clear]" @click="message = ''">
                  <i class="fa-solid fa-xmark" />
                </button>
                <button
                  :class="[s.blue, s.button]"
                  @click="sendMessage"
                  :disabled="!message.trim()">
                  <i class="fa-solid fa-paper-plane me-2" />送信
                </button>
              </div>
            </div>
          </div>

          <!-- メッセージ一覧（下部） -->
          <div :class="s.messages_container">
            <!-- さらに読み込むボタン（上部） -->
            <div v-if="hasMore" class="text-center mb-3">
              <button
                :class="[s.gray, s.button]"
                @click="loadMore"
                :disabled="flag.loading">
                <i class="fa-solid fa-arrow-down me-2" />さらに読み込む
              </button>
            </div>

            <div v-if="unreadChatMessages?.length">
              <div :class="s.chats" v-for="message in unreadChatMessages" :key="message.id">
                <li :class="message.fromUser.id === user.id ? s.q : s.a">
                  <div :class="[s.mWrap, message.fromUser.id === user.id ? s.question : s.answer]">
                    <div :class="s.name">
                      {{ message.fromUser.username }}
                      [{{ formatTimestamp(message.created_at, 'YYYY/MM/DD HH:mm') }}]
                    </div>
                    <div>
                      <p :class="s.message" v-html="message.message"></p>
                    </div>
                  </div>
                </li>
              </div>
            </div>
            <div v-else class="text-center py-4">
              <p class="text-muted">メッセージはまだありません</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <div class="alert alert-warning">
        <i class="fa-solid fa-triangle-exclamation me-1"></i>
        こちらのチャットは現在利用できません。
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import cf from '@/mixins/commonFunctions';

export default {
  name: 'chat',
  mixins: [cf],
  data() {
    return {
      flag: {
        showSlider: true,
        loading: false,
      },
      message: '',
      unreadChatMessages: [],
      hasMore: false,
      currentPage: 1,
      perPage: 30,
    };
  },
  computed: {
    ...mapState(['user', 'helper', 'modal']),

    // チャット種別の判定
    chatType() {
      const userId = this.$route.params.userId;
      const reservationId = Number(this.$route.params.reservationId);

      if (userId === 'operator') {
        return reservationId === 0 ? 'operator-only' : 'operator-with-reservation';
      }
      return 'customer';
    },

    isOperatorChat() {
      return this.chatType === 'operator-only' || this.chatType === 'operator-with-reservation';
    },

    officeId() {
      return this.user.offices?.[0]?.id;
    },

    pageTitle() {
      if (this.chatType === 'operator-only') return 'オペレーターとのチャット';
      if (this.chatType === 'operator-with-reservation') return '予約に関するチャット（対オペレーター）';
      return 'チャット';
    },

    reservation() {
      // オペレーターのみチャットの場合はnull
      if (this.chatType === 'operator-only') return null;

      const driverReservations = this.user.orders || [];
      const routeParams = this.$route.params;
      const paramsReservationId = Number(routeParams.reservationId);

      // 顧客チャットの場合
      if (this.chatType === 'customer') {
        const paramsUserId = Number(routeParams.userId);
        const targetReservation = driverReservations.find((rsv) => (
          rsv.id === paramsReservationId
          && ((rsv.foreign_type === 11 && rsv.foreign_id === paramsUserId)
            || (rsv.foreign_type === 31 && rsv.demandCustomers?.some((customer) => customer.user_id === paramsUserId)))
        ));

        // LineUserが存在しない場合は表示しない
        if (targetReservation?.foreign_type === 11 && !targetReservation?.user?.lineUser?.id) return null;
        if (targetReservation?.foreign_type === 31) {
          const dc = targetReservation?.demandCustomers?.find((customer) => customer.user_id === paramsUserId);
          if (!dc || !dc?.user?.lineUser?.id) return null;
        }
        return targetReservation || null;
      }

      // オペレーターとの予約チャットの場合
      if (this.chatType === 'operator-with-reservation') {
        const targetReservation = driverReservations.find((rsv) => rsv.id === paramsReservationId);
        return targetReservation || null;
      }

      return null;
    },
  },
  watch: {
    user: {
      handler() {
        this.hideLoading();
      },
      deep: true,
    },
    reservation: {
      handler(newVal) {
        if (this.chatType === 'customer' && newVal && newVal.id) {
          this.getChatMessage();
        } else if (this.chatType === 'operator-with-reservation' && newVal && newVal.id) {
          this.getChatMessage();
        } else if (this.chatType === 'operator-only') {
          this.getChatMessage();
        }
      },
      immediate: true,
    },
  },
  methods: {
    async getChatMessage(isLoadMore = false) {
      // 顧客チャットの場合は予約が必須
      if (this.chatType === 'customer' && !this.reservation?.id) return;

      this.flag.loading = true;

      const params = {
        flags: [1],
        page: isLoadMore ? this.currentPage + 1 : 1,
        limit: this.perPage,
        officeId: this.officeId,
      };

      // 予約IDがある場合/ない場合の絞り込み
      if (this.chatType === 'customer' && this.reservation?.id) {
        // 顧客との予約チャットは対象予約のみ
        params.reservationId = this.reservation.id;
      } else if (this.chatType === 'operator-with-reservation' && this.reservation?.id) {
        // 予約に関する、対OP: reservation_idがある
        params.reservationId = this.reservation.id;
      } else if (this.chatType === 'operator-only') {
        // 予約に関係ない、対OPのみ: reservation_id IS NULL を明示
        params.reservationIsNull = 1;
      }

      // userIdを設定
      if (this.chatType === 'customer') {
        // 顧客チャットの場合: 顧客IDを指定して、顧客が関与する全メッセージを取得
        // これにより driver⇔customer、operator⇔customer の両方が表示される
        const customerId = this.reservation.foreign_type === 11
          ? this.reservation.user.id
          : Number(this.$route.params.userId);
        params.userId = customerId;
      } else {
        // オペレーターチャットの場合: ドライバーIDを指定して、driver⇔operator のメッセージのみ取得
        params.userId = this.user.id;
        params.operatorChatOnly = 1; // 顧客とのメッセージを除外
      }

      try {
        const response = await this.axios({
          method: 'GET',
          url: '/v1/message/get/list',
          params,
        });

        const res = response.data;
        let newMessages = res.messages.data;

        // ホームのオペレーターとチャット（予約に関係ない）の場合は reservation_id が null のみ表示
        if (this.chatType === 'operator-only') {
          newMessages = newMessages.filter((m) => m.reservation_id == null);
        }

        if (isLoadMore) {
          this.unreadChatMessages = [...this.unreadChatMessages, ...newMessages];
          this.currentPage += 1;
        } else {
          this.unreadChatMessages = newMessages;
          this.currentPage = 1;
        }

        this.hasMore = res.messages.meta.current_page < res.messages.meta.last_page;
      } catch (error) {
        console.error('メッセージの取得に失敗しました', error);
      } finally {
        this.flag.loading = false;
      }
    },

    loadMore() {
      this.getChatMessage(true);
    },
    async sendMessage() {
      if (!this.message.trim()) return;

      this.showLoading();

      const data = {
        flag: 1,
        from_user_id: this.user.id,
        office_id: this.officeId,
        message: this.message.trim(),
      };

      // オペレーターチャットの場合
      if (this.isOperatorChat) {
        data.to_user_id = null; // operator宛て
        // 予約IDがあれば追加
        if (this.reservation?.id) {
          data.reservation_id = this.reservation.id;
        }
      } else {
        // 顧客チャットの場合
        const toUserId = this.reservation.foreign_type === 11
          ? this.reservation.user.id
          : Number(this.$route.params.userId);
        data.to_user_id = toUserId;
        data.reservation_id = this.reservation.id;
      }

      try {
        const sendResult = await this.axios({
          method: 'POST',
          url: '/v1/message/create',
          data,
        });

        if (!sendResult.data.sended) throw Error('failed to send message.');

        this.message = '';

        // メッセージを再取得して更新
        await this.getChatMessage();

        alert('メッセージを送信しました。');
      } catch (error) {
        console.error('メッセージの送信に失敗しました', error);
        if (error.response?.data?.message === 'Customer line user not found.') {
          alert('該当の顧客にはLINE連携が設定されていません。');
        } else {
          alert('エラーが発生しました。再度お試しください。');
        }
      } finally {
        this.hideLoading();
      }
    },
    updateUser() {
      this.showLoading();
      this.$store.dispatch('user/update', { hideLoading: true });
    },
    getBackRoute() {
      return this.reservation ? `/order?code=${this.reservation.code || ''}` : '/';
    },
    /** ローディング表示 */
    showLoading() {
      const args = { modalName: 'modalLoadingBallScaleRippleMultiple' };
      this.$store.dispatch('modal/loadings/showModal', args, { root: true });
    },
    /** ローディング終了 */
    hideLoading() {
      this.$store.dispatch('modal/loadings/hideModal', null, { root: true });
    },
  },
};
</script>

<style lang="scss" module="s">
.wrap {
  height: 100%;
  .content {
    height: 100%;
  }
}

.card_container {
  padding: 0 60px;
  height: 100%;
  .slide {
    height: 100%;
  }
}

.card {
  height: 100%;
  // box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 12px;
  // border-radius: 8px;
  display: flex;
  flex-direction: column;
  // border: 1px solid var(--border-gray);
  &_header {
    border-bottom: 1px solid var(--border-gray);
    padding: 4px 12px;
    background-color: var(--bg-gray);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-shrink: 0;
  }
  &_body {
    flex: 1;
    padding: 0 12px;
    overflow: scroll;
    background-color: #ffffff;
    &_content {
      height: 100%;
      overflow: scroll;
    }
  }
}

.reservation {
  position: relative;
  height: fit-content;
  display: flex;
  padding-top: 12px;
  > :nth-child(1) {
    flex: 1;
  }
  > :nth-child(2) {
    width: 150px;
    display: flex;
    flex-direction: column;
    > :not(:first-child) {
      margin-top: 6px;
    }
  }
}

.item {
  width: 100%;
  text-align: left;
  border-radius: 4px;
  margin-bottom: 12px;
  > :nth-child(1) {
    display: flex;
    align-items: center;
    background-color: var(--bg-gray);
    padding: 2px 8px;
    > * {
      font-weight: bold;
    }
  }
  > :nth-child(2) {
    padding: 0 8px;
    margin-top: 8px;
    flex: 1;
  }
}

.revisions {
  margin-left: 0;
}

// windowサイズが縦長の時
.wrap.vertical {
  .card_container {
    padding: 0  0 60px 0;
  }
  .reservation {
    flex-direction: column;
  }
}
.buttons {
  height: 100%;
  > * {
    border: none;
    background-color: transparent;
    padding: 0 16px;
    height: 100%;
    font-weight: bold;
    border-radius: 4px;
    &.green {
      background-color: #20C997;
      color: white;
    }
  }
}
.chat {
  border: 1px solid var(--border-gray);
  border-radius: 8px;
  display: flex;
  flex-direction: column;

  .label {
    background-color: var(--bg-gray);
    padding: 12px;
    font-weight: bold;
    flex-shrink: 0;
  }

  .form_container {
    border-bottom: 2px solid #dee2e6;
    padding: 0;
    flex-shrink: 0;

    .templates {
      width: 100%;
      padding: 12px 12px 0 12px;
      display: flex;
      flex-wrap: wrap;
      margin: 0;
      list-style: none;

      > li {
        padding: 4px 12px;
        margin-top: 6px;
        margin-right: 6px;
      }
    }

    .form {
      padding: 12px;
      display: flex;
      flex-direction: column;

      textarea {
        width: 100%;
        padding: 12px;
        border: 1px solid #ced4da;
        border-radius: 6px;
        font-size: 14px;
        resize: vertical;
        font-family: inherit;

        &:focus {
          outline: none;
          border-color: #80bdff;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }
      }

      button {
        height: 50px;
        padding: 0 16px;
        margin-left: 6px;

        &.clear {
          width: 50px !important;
          padding: 0;
        }
      }
    }
  }

  .messages_container {
    flex: 1;
    padding: 10px 15px;

    .chats {
      margin-bottom: 24px;

      > li {
        list-style: none;
      }

      &:not(:first-child) > li {
        position: relative;

        &::before {
          content: "";
          position: absolute;
          top: -24px;
          left: 30px;
          width: 3px;
          height: 24px;
        }

        &.q::before {
          background-color: #25db2e;
        }

        &.a::before {
          background-color: #26aac7;
        }
      }

      .mWrap {
        padding: 15px;
        border-radius: 8px;
      }

      .name {
        font-size: 16px;
        margin-bottom: 4px;
      }

      .message {
        font-size: 16px;
        white-space: pre-wrap;
        word-break: break-all;
        margin: 0;
      }

      .question {
        background-color: #e2f5df;
      }

      .answer {
        background-color: #daeef2;
      }
    }
  }
}

.button {
  border: none;
  background-color: transparent;
  padding: 0 16px;
  height: 50px;
  font-weight: bold;
  border-radius: 4px;
  white-space: nowrap;
  &:disabled {
    position: relative;
    &::after {
      content: '';
      position: absolute;
      top: -4px; left: -4px;
      width: calc(100% + 4px * 2);
      height: calc(100% + 4px * 2);
      border-radius: 4px;
      background-color: rgba(#000000, .5);
      mix-blend-mode: multiply;
    }
  }
  &.blue {
    background-color: var(--primary-blue);
    color: white;
  }
  &.red {
    background-color: var(--primary-red);
    color: white;
  }
  &.green {
    background-color: var(--primary-green);
    color: white;
  }
  &.gray {
    background-color: var(--primary-gray);
    color: white;
  }
  &.back {
    height: 70px;
  }
}
</style>
